import { isValidEmail } from '../../lib/validation';
import { PASSWORD_POLICY } from './authPasswordPolicy';
import type { LoginPayload } from './authTypes';

/**
 * Les valeurs de LoginFormErrors sont des clés i18n du namespace 'common'.
 * Le composant appelant est responsable de la traduction : t(errors.username).
 * Cette fonction reste pure (pas de dépendance React/i18next).
 */
export type LoginFormErrors = Partial<Record<keyof LoginPayload, string>>;

export function validateLoginForm(values: LoginPayload): LoginFormErrors {
  const errors: LoginFormErrors = {};

  if (!values.username.trim()) {
    errors.username = 'auth.validation.usernameRequired';
  }

  // Login only requires a non-empty password (matches the backend `AuthLoginDto`,
  // which is `@IsNotEmpty()` with no length rule). The 12-char policy applies to
  // *setting* a password (reset), never to entering an existing one.
  if (!values.password) {
    errors.password = 'auth.validation.passwordRequired';
  }

  return errors;
}

export function isLoginFormValid(errors: LoginFormErrors): boolean {
  return Object.keys(errors).length === 0;
}

// Same shape as the login validators: returns an i18n key (namespace 'common')
// or undefined. Kept pure so the caller owns translation. Reuses the shared
// `isValidEmail` (src/lib/validation) — single source of truth for the format.
export function validateEmail(email: string): string | undefined {
  const value = email.trim();
  if (!value) return 'auth.validation.emailRequired';
  if (!isValidEmail(value)) return 'auth.validation.emailInvalid';
  return undefined;
}

/**
 * Validates a new password against {@link PASSWORD_POLICY} (the single editable source
 * of the rules, mirroring the backend `ResetPasswordDto`). Returns an i18n key or
 * undefined. Each complexity check is guarded by its policy flag so the policy stays
 * data-driven from that one constant.
 */
export function validatePassword(password: string): string | undefined {
  if (!password) return 'auth.validation.passwordRequired';
  if (password.length < PASSWORD_POLICY.minLength) return 'auth.validation.passwordPolicyMinLength';
  if (password.length > PASSWORD_POLICY.maxLength) return 'auth.validation.passwordMaxLength';
  if (PASSWORD_POLICY.requireUppercase && !/[A-Z]/.test(password)) {
    return 'auth.validation.passwordComplexity';
  }
  if (PASSWORD_POLICY.requireLowercase && !/[a-z]/.test(password)) {
    return 'auth.validation.passwordComplexity';
  }
  if (PASSWORD_POLICY.requireDigit && !/\d/.test(password)) {
    return 'auth.validation.passwordComplexity';
  }
  if (PASSWORD_POLICY.requireSpecial && !/[^A-Za-z0-9]/.test(password)) {
    return 'auth.validation.passwordComplexity';
  }
  return undefined;
}

export interface ResetPasswordFormValues {
  password: string;
  confirmPassword: string;
}

export type ResetPasswordFormErrors = Partial<Record<keyof ResetPasswordFormValues, string>>;

/** Validates the reset-password form: policy on the new password + confirmation match. */
export function validateResetPasswordForm(
  values: ResetPasswordFormValues,
): ResetPasswordFormErrors {
  const errors: ResetPasswordFormErrors = {};

  const passwordError = validatePassword(values.password);
  if (passwordError) errors.password = passwordError;

  if (!values.confirmPassword) {
    errors.confirmPassword = 'auth.validation.passwordConfirmRequired';
  } else if (values.confirmPassword !== values.password) {
    errors.confirmPassword = 'auth.validation.passwordMismatch';
  }

  return errors;
}

export function isResetPasswordFormValid(errors: ResetPasswordFormErrors): boolean {
  return Object.keys(errors).length === 0;
}
